import { ApexOptions } from 'apexcharts'
import BaseChartCard from 'common/components/BaseChartCard'
import { createArrayOfNumber } from 'common/utils/array'
import { getChartGridConfig } from 'common/utils/chart'
import { setupChartInteractions } from 'common/utils/chartInteraction'
import { formatThaiShortDateTwoDigitYear } from 'common/utils/date'
import {
  ComparisonCycle,
  DataPoint,
  FoodStandardResponse,
  GrowthStandardResponse,
} from 'modules/comparisonPond/api/types'
import React, { useMemo } from 'react'
import ReactApexChart from 'react-apexcharts'
import { useTranslation } from 'react-i18next'
import { FONT_PRIMARY } from 'theme/typography'

import { Box, Skeleton, Stack } from '@mui/material'

interface ChartData {
  cycles?: ComparisonCycle[]
  data_points?: DataPoint[]
}

interface ComparisonChartProps {
  title: string
  cycleData?: ChartData
  standardData?: FoodStandardResponse[] | GrowthStandardResponse[] | null
  isLoading: boolean
  yUnit?: string
}

const SVG_HEIGHT = 300
const Y_AXIS_PADDING_MULTIPLIER = 1.25
const Y_TICK_AMOUNT = 5
const X_TICK_AMOUNT = 5

const ComparisonChart: React.FC<ComparisonChartProps> = ({
  title,
  cycleData,
  standardData,
  isLoading,
  yUnit = '',
}) => {
  const { t } = useTranslation('pondComparison')

  const cycles = cycleData?.cycles ?? []

  const dataPoints = cycleData?.data_points ?? []
  const standards = (standardData ?? []) as Array<FoodStandardResponse | GrowthStandardResponse>
  const speciesName = cycles.length ? cycles[0].species?.name : ''

  // lookups: per-series age→value map, used by custom tooltip to find values by age (not by index)
  const { series, lookups } = useMemo(() => {
    if (isLoading)
      return { series: [] as ApexAxisChartSeries, lookups: [] as Map<number, number>[] }

    const rawSeriesList: { name: string; data: Map<number, number> }[] = []

    // --- Process Standards ---
    if (standards.length > 0) {
      const standardMap = new Map<number, number>()
      standards.forEach((std) => {
        const value =
          'weight_in_g' in std ? std.weight_in_g : 'total_foods' in std ? std.total_foods : null
        if (value !== null && value !== undefined) {
          standardMap.set(Math.round(std.age), Number(value))
        }
      })
      if (standardMap.size > 0) {
        rawSeriesList.push({ name: t('standard'), data: standardMap })
      }
    }

    // --- Process Cycles ---
    cycles.forEach((cycle, cycleIndex) => {
      if (!cycle.farm_name || !cycle.pond_name || !cycle.start_date) return
      const cycleMap = new Map<number, number>()
      dataPoints.forEach((dt) => {
        const val = dt.values[cycleIndex]
        if (val !== null && val !== undefined) {
          cycleMap.set(Math.round(dt.age), Number(val))
        }
      })
      if (cycleMap.size > 0) {
        const startDate = formatThaiShortDateTwoDigitYear(cycle.start_date)
        const endDate = cycle.end_date
          ? formatThaiShortDateTwoDigitYear(cycle.end_date)
          : t('present')
        rawSeriesList.push({
          name: `${cycle.farm_name} ${cycle.pond_name} (${startDate} - ${endDate})`,
          data: cycleMap,
        })
      }
    })

    // Sparse series: only actual data points per series so the line connects naturally
    // without gaps, while the custom tooltip resolves values by age (not by array index).
    const sparseSeries: ApexAxisChartSeries = rawSeriesList.map((s) => ({
      name: s.name,
      data: Array.from(s.data.entries())
        .sort(([a], [b]) => a - b)
        .map(([age, val]) => ({ x: age, y: val })),
    }))

    return { series: sparseSeries, lookups: rawSeriesList.map((s) => s.data) }
  }, [isLoading, cycles, dataPoints, standards, t])

  const { yMin, yMax } = useMemo(() => {
    const hasData = series.length > 0

    if (!hasData) {
      return { yMin: 0, yMax: 500 }
    }

    const allValues: number[] = []
    series.forEach((s) => {
      s.data.forEach((v) => {
        if (v && typeof v === 'object' && 'y' in v) {
          const yValue = v.y
          if (typeof yValue === 'number' && !isNaN(yValue)) {
            allValues.push(yValue)
          }
        }
      })
    })

    if (allValues.length === 0) {
      return { yMin: 0, yMax: 500 }
    }

    const maxValue = Math.max(...allValues)
    return {
      yMin: 0,
      yMax: Math.ceil(maxValue * Y_AXIS_PADDING_MULTIPLIER),
    }
  }, [series])

  const chartOptions: ApexOptions = useMemo(() => {
    const hasData = series.length > 0

    return {
      chart: {
        type: 'line',
        toolbar: {
          show: false,
          autoSelected: 'pan',
        },
        fontFamily: FONT_PRIMARY,
        zoom: {
          enabled: false,
          type: 'x',
          autoScaleYaxis: true,
        },
        animations: {
          enabled: true,
        },
        events: {
          mounted: (chartContext: any, config: any) => {
            setupChartInteractions(chartContext, config)
          },
        },
      },
      grid: getChartGridConfig(),
      stroke: {
        curve: 'straight',
        width: 2,
      },
      markers: {
        size: 0,
        hover: { sizeOffset: 4 },
      },
      xaxis: {
        type: 'numeric',
        tickAmount: X_TICK_AMOUNT,
        title: {
          text: t('age.day', { speciesName }),
          style: { fontFamily: FONT_PRIMARY, fontSize: '12px' },
        },
        labels: {
          style: { fontFamily: FONT_PRIMARY },
          showDuplicates: true,
          formatter: (val) => Number(val).toFixed(0),
        },
        tooltip: {
          enabled: false,
        },
      },
      yaxis: {
        min: yMin,
        max: yMax,
        tickAmount: Y_TICK_AMOUNT,
        forceNiceScale: true,
        title: {
          text: yUnit,
          style: {
            fontFamily: FONT_PRIMARY,
            fontSize: '12px',
            fontWeight: 600,
          },
        },
        labels: {
          style: {
            fontFamily: FONT_PRIMARY,
          },
          formatter: (val: number) => {
            if (typeof val !== 'number' || isNaN(val)) return '0.00'
            return new Intl.NumberFormat('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(val)
          },
        },
      },
      legend: {
        show: hasData,
        position: 'bottom',
        horizontalAlign: 'left',
        fontSize: '12px',
        fontFamily: FONT_PRIMARY,
      },
      tooltip: {
        intersect: false,
        shared: false,
        custom: ({
          dataPointIndex,
          seriesIndex,
          w,
        }: {
          dataPointIndex: number
          seriesIndex: number
          w: { globals: { seriesX: number[][]; colors: string[] } }
        }) => {
          const age: number = w.globals.seriesX[seriesIndex]?.[dataPointIndex]
          if (age === undefined) return ''

          const header = `${t('age', { speciesName })}: ${age} ${t('unit.day')}`
          const rows = lookups
            .map((lookup, idx) => {
              const val = lookup.get(age)
              if (val === undefined) return ''
              const formatted = new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(val)
              const color: string = w.globals.colors?.[idx] ?? '#999'
              const name: string = series[idx]?.name ?? ''
              return `<div style="display:flex;align-items:center;gap:6px;padding:2px 0">
                <span style="width:10px;height:10px;border-radius:50%;background:${color};flex-shrink:0;display:inline-block;"></span>
                <span>${name}: <strong>${formatted}</strong></span>
              </div>`
            })
            .join('')

          return `<div style="padding:8px 10px;font-family:${FONT_PRIMARY};font-size:12px;min-width:160px;">
            <div style="font-weight:600;margin-bottom:4px;">${header}</div>
            ${rows}
          </div>`
        },
      },
    }
  }, [series, lookups, yMin, yMax, yUnit, t, speciesName])

  return (
    <BaseChartCard title={title}>
      <Box
        sx={{
          width: '100%',
          fontSize: '16px',
          fontFamily: FONT_PRIMARY,
          '& .apexcharts-legend-series': { display: 'block', width: '100%' },
          '& .apexcharts-legend': { textAlign: 'left' },
        }}
      >
        {isLoading ? (
          <Stack spacing={3} p={2}>
            <Stack direction="row" justifyContent="flex-start" spacing={1}>
              {createArrayOfNumber(3).map((i) => (
                <Skeleton width={60} key={i} />
              ))}
            </Stack>
            {createArrayOfNumber(6).map((i) => (
              <Stack direction="row" key={i} spacing={1}>
                <Skeleton width={40} />
                <Skeleton width="100%" />
              </Stack>
            ))}
            <Stack direction="row" spacing={2}>
              {createArrayOfNumber(6).map((i) => (
                <Skeleton key={i} width="100%" />
              ))}
            </Stack>
          </Stack>
        ) : (
          <ReactApexChart options={chartOptions} series={series} type="line" height={SVG_HEIGHT} />
        )}
      </Box>
    </BaseChartCard>
  )
}
export default ComparisonChart
